import { useRef, useState } from "react";
import { Upload, Wand2, Download, RefreshCw, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";

export default function BrandingProposal({ model, brandName, onGenerated }) {
    const { tr } = useLocale();
    const inputRef = useRef(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [resultUrl, setResultUrl] = useState(null);
    const [error, setError] = useState(null);

    function onPick(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!/\.(png|jpe?g|webp)$/i.test(file.name)) {
            toast.error(tr("Formato não suportado (png, jpg, webp).", "Unsupported format (png, jpg, webp)."));
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error(tr("Logo muito grande (máx 5 MB).", "Logo too large (max 5 MB)."));
            return;
        }
        setLogoFile(file);
        setResultUrl(null);
        setError(null);
        const reader = new FileReader();
        reader.onload = () => setLogoPreview(reader.result);
        reader.readAsDataURL(file);
    }

    async function generate() {
        if (!logoFile || generating) return;
        setGenerating(true);
        setResultUrl(null);
        setError(null);
        try {
            const fd = new FormData();
            fd.append("logo", logoFile);
            fd.append("model", model);
            fd.append("brand_name", brandName || "");
            const res = await fetch(`${API}/branding-proposal`, { method: "POST", body: fd });
            let data = null;
            try { data = await res.json(); } catch { /* resposta não-JSON (ex: proxy) */ }
            if (!res.ok) throw new Error(typeof data?.detail === "string" ? data.detail : "erro");
            setResultUrl(`${API.replace("/api", "")}${data.url}`);
            onGenerated?.(data.id);
            toast.success(tr("Proposta visual gerada!", "Visual proposal generated!"));
        } catch (err) {
            const msg = typeof err?.message === "string" && err.message !== "erro"
                ? err.message
                : tr("Não foi possível gerar a proposta. Tente novamente com outro logo (PNG/JPG nítido, fundo simples).",
                    "Could not generate the proposal. Try again with a different logo (clear PNG/JPG, simple background).");
            setError(msg);
            toast.error(msg);
        } finally {
            setGenerating(false);
        }
    }

    return (
        <div className="mt-6 rounded-xl border border-secondary/40 bg-gradient-to-br from-[#FDF8EC] to-white p-5" data-testid="branding-proposal">
            <div className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-secondary" />
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary">
                    {tr("Proposta visual com IA", "AI visual proposal")}
                </span>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-extrabold text-primary">{tr("NOVO", "NEW")}</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                {tr(
                    "Envie o logo da sua marca e veja, em segundos, o carrinho com o seu branding aplicado.",
                    "Upload your brand logo and see, in seconds, the cart with your branding applied."
                )}
            </p>

            <input ref={inputRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={onPick} data-testid="branding-logo-input" />

            <div className="mt-4 flex flex-wrap items-center gap-3">
                <button type="button" onClick={() => inputRef.current?.click()} data-testid="branding-upload-btn"
                    className="inline-flex items-center gap-2 rounded-md border border-primary px-4 py-2.5 text-xs font-bold text-primary hover:bg-primary hover:text-white transition">
                    <Upload className="h-3.5 w-3.5" /> {logoFile ? tr("Trocar logo", "Change logo") : tr("Enviar logo", "Upload logo")}
                </button>
                {logoPreview && (
                    <img src={logoPreview} alt="logo" className="h-11 w-11 rounded-md object-contain border border-border bg-white p-1" data-testid="branding-logo-preview" />
                )}
                {logoFile && (
                    <button type="button" onClick={generate} disabled={generating} data-testid="branding-generate-btn"
                        className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-xs font-bold text-white hover:-translate-y-0.5 transition disabled:opacity-60">
                        {generating
                            ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> {tr("A criar a proposta… (~30s)", "Creating proposal… (~30s)")}</>
                            : <><Wand2 className="h-3.5 w-3.5" /> {tr("Gerar proposta visual", "Generate visual proposal")}</>}
                    </button>
                )}
            </div>

            {error && !generating && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-xs text-red-700" data-testid="branding-error">
                    <p className="font-bold">{tr("A geração falhou", "Generation failed")}</p>
                    <p className="mt-1 leading-relaxed">{error}</p>
                    <p className="mt-1 text-red-500">
                        {tr("Dica: use um logo com pelo menos 256 px, em PNG, JPG ou WEBP.", "Tip: use a logo of at least 256 px, in PNG, JPG or WEBP.")}
                    </p>
                </div>
            )}

            {generating && (
                <div className="mt-4 flex items-center gap-3 rounded-lg bg-white border border-border p-4 text-xs text-muted-foreground" data-testid="branding-generating">
                    <ImageIcon className="h-4 w-4 text-secondary animate-pulse" />
                    {tr("A nossa IA está a aplicar o seu logo e as cores da marca ao carrinho…", "Our AI is applying your logo and brand colours to the cart…")}
                </div>
            )}

            {resultUrl && (
                <div className="mt-4" data-testid="branding-result">
                    <img src={resultUrl} alt={tr("Proposta visual do carrinho", "Cart visual proposal")}
                        className="w-full rounded-xl border border-border shadow-md" />
                    <div className="mt-3 flex flex-wrap gap-2">
                        <a href={resultUrl} download="novifood-proposta.png" data-testid="branding-download"
                            className="inline-flex items-center gap-2 rounded-md bg-secondary px-4 py-2 text-xs font-bold text-primary hover:-translate-y-0.5 transition">
                            <Download className="h-3.5 w-3.5" /> {tr("Descarregar proposta", "Download proposal")}
                        </a>
                        <button type="button" onClick={generate} disabled={generating}
                            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-xs font-bold text-primary hover:bg-muted transition">
                            <RefreshCw className="h-3.5 w-3.5" /> {tr("Gerar outra versão", "Generate another version")}
                        </button>
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                        {tr("Imagem ilustrativa gerada por IA — o design final é afinado pela nossa equipa.", "AI-generated illustrative image — the final design is refined by our team.")}
                    </p>
                </div>
            )}
        </div>
    );
}
